/**
 * The two things a customer is told when a VM deletion ends: it was deleted (and until when it is
 * kept), or the infrastructure refused and the machine is unchanged.
 *
 * A deletion ends out of sight of the page that started it — the delete job asks n8n in the
 * background — so the outcome reaches the console by two roads: the `resource:status` socket
 * event, heard by the global listener in `useVmDeletionNotifications`, and the status watch a
 * page runs as a fallback (`useResourceStatusWatch`). Both land here, and this registry is what
 * keeps one outcome from being announced twice when both roads deliver it.
 */

import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "i18next";

import type { ResourceStatusEvent } from "@/lib/socketio";
import { formatCustomerTime, parseUtcMs } from "@/lib/customerTime";
import type { Resource } from "@/types/resource";

/** Socket failure code the delete job sends when the infrastructure refused to delete a VM for good. */
export const DELETION_FAILED_CODE = "DeletionFailed";

type DeletionOutcome = "deleted" | "failed";

/**
 * How far apart two reports of the same outcome can be dated and still be one outcome.
 *
 * The two roads date it differently: the socket event carries its publish time, the status read
 * carries `deletedAt` / `deletionFailedAt` from the row — seconds apart for one deletion. A new
 * deletion of the same VM can only follow a refused one, whose retries take minutes, so a later
 * outcome falls outside the window and is announced again.
 */
const SAME_OUTCOME_WINDOW_MS = 5 * 60 * 1000;

/** Keyed `${resourceId}:${outcome}`; the value is the instant of the last outcome announced. */
const announced = new Map<string, number>();

/**
 * Records an outcome, and says whether it is news. False when the same outcome of the same VM was
 * already announced within {@link SAME_OUTCOME_WINDOW_MS} of `at`.
 */
function claim(
  resourceId: string,
  outcome: DeletionOutcome,
  at: string | null | undefined,
): boolean {
  const key = `${resourceId}:${outcome}`;
  let instant = Date.now();
  if (at) {
    const parsed = parseUtcMs(at);
    if (parsed !== null) {
      instant = parsed;
    }
  }

  const previous = announced.get(key);
  if (previous !== undefined) {
    const sameOutcome = Math.abs(instant - previous) < SAME_OUTCOME_WINDOW_MS;
    if (sameOutcome) {
      return false;
    }
  }

  announced.set(key, instant);
  return true;
}

interface VmDeletedNotice {
  resourceId: string;
  name: string;
  /** Until when the deleted VM is kept; the plain sentence is used when unknown. */
  restorableUntil?: string | null;
  /** When the deletion was confirmed: the row's `deletedAt`, or the socket event's timestamp. */
  at?: string | null;
}

interface VmDeletionFailedNotice {
  resourceId: string;
  name: string;
  /** When the refusal happened: the row's `deletionFailedAt`, or the socket event's timestamp. */
  at?: string | null;
}

/** Tells the customer a VM was deleted and until when it is kept — once per deletion. */
export function notifyVmDeleted(notice: VmDeletedNotice): void {
  if (!claim(notice.resourceId, "deleted", notice.at)) {
    return;
  }

  let until: string | null = null;
  if (notice.restorableUntil) {
    until = formatCustomerTime(notice.restorableUntil);
  }

  if (until) {
    toast.success(
      i18n.t("vm.deletion.deletedUntilToast", {
        ns: "resources",
        name: notice.name,
        until,
      }),
    );
    return;
  }

  toast.success(
    i18n.t("vm.deletion.deletedToast", { ns: "resources", name: notice.name }),
  );
}

/** Tells the customer the infrastructure refused to delete a VM and it is unchanged — once. */
export function notifyVmDeletionFailed(notice: VmDeletionFailedNotice): void {
  if (!claim(notice.resourceId, "failed", notice.at)) {
    return;
  }

  toast.error(
    i18n.t("vm.deletion.failedToast", { ns: "resources", name: notice.name }),
    { duration: 10000 },
  );
}

/** Forgets every announced outcome. Tests only. */
export function resetVmDeletionNotices(): void {
  announced.clear();
}

/**
 * Forgets any outcome already announced for one VM, so its next deletion announces its own
 * outcome even if it lands inside {@link SAME_OUTCOME_WINDOW_MS} of the last one.
 *
 * The window alone handles the ordinary case — a refused deletion's retries take minutes — but not
 * a fast delete-again (support acting quickly, or an end-to-end test), where the second
 * deletion's outcome would otherwise read as a repeat of the first and go unannounced. Called from
 * both the roads that can observe a fresh deletion starting: the delete mutation succeeding, and
 * either the status watch or the socket event next seeing the row Terminating again.
 */
export function forgetVmDeletionOutcome(resourceId: string): void {
  announced.delete(`${resourceId}:deleted`);
  announced.delete(`${resourceId}:failed`);
}

/**
 * Whether this client last saw the resource as Terminating: in its detail cache, or as a row of any
 * cached list (the VM list page holds no detail cache).
 */
function wasSeenTerminating(
  queryClient: QueryClient,
  resourceId: string,
): boolean {
  const detail = queryClient.getQueryData<Resource>([
    "resources",
    "detail",
    resourceId,
  ]);
  if (detail?.status === "Terminating") {
    return true;
  }

  const lists = queryClient.getQueriesData<{ resources?: Resource[] }>({
    queryKey: ["resources"],
  });
  for (const [, data] of lists) {
    const row = data?.resources?.find((resource) => resource.id === resourceId);
    if (row?.status === "Terminating") {
      return true;
    }
  }

  return false;
}

/**
 * Answers per event object. Several handlers receive the same event — the page's
 * `useResourceSocket` and the global listener — and the page's drops the detail cache the
 * fallback below reads, so whichever handler runs first decides for all of them.
 */
const confirmedByEvent = new WeakMap<ResourceStatusEvent, boolean>();

/**
 * True when a status event is the end of a VM deletion rather than the start of a reinstall.
 *
 * Both publish Decommissioning; the delete job marks its own with `outcome: "Deleted"`. Events
 * from older jobs carry no outcome, and for those only the status before tells them apart: a
 * deletion leaves Terminating, while a reinstall starts from a row in the Deleted tab that no
 * live cache holds.
 */
export function isVmDeletionConfirmed(
  event: ResourceStatusEvent,
  queryClient: QueryClient,
): boolean {
  const known = confirmedByEvent.get(event);
  if (known !== undefined) {
    return known;
  }

  let confirmed = false;
  if (event.status === "Decommissioning") {
    // Present at all (null included) means a job that knows the field, so it is authoritative.
    const sentByOlderJob = event.outcome === undefined;
    if (sentByOlderJob) {
      confirmed = wasSeenTerminating(queryClient, event.resourceId);
    } else {
      confirmed = event.outcome === "Deleted";
    }
  }

  confirmedByEvent.set(event, confirmed);
  return confirmed;
}
